import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi, getImageUrl } from '../lib/api';
import { downloadPdfWithCache } from '../lib/pdf';
import { devError } from '../lib/logger';
import { iterateSectionCards, cardCode, cardName, cardShort, cardImage } from '../lib/grouping';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Download, Image as ImageIcon, BookOpen, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import '@/App.css';

// ---- Corporate (MOQ) helpers — render-only, no state ----------------------
function formatDateForCover(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function CorporateCoverMeta({ meta }) {
  if (!meta) return null;
  const client = (meta.client_company || '').trim();
  const contact = (meta.contact_person || '').trim();
  const validity = formatDateForCover((meta.validity_date || '').trim());
  const delivery = (meta.delivery_terms || '').trim();
  const gst = (meta.gst_note || '').trim();
  const custom = (meta.custom_note || '').trim();
  const pairs = [
    contact && { k: 'Contact', v: contact },
    validity && { k: 'Valid until', v: validity },
    delivery && { k: 'Delivery', v: delivery },
    gst && { k: 'GST', v: gst },
  ].filter(Boolean);
  if (!client && !pairs.length && !custom) return null;
  return (
    <div className="max-w-2xl mx-auto mt-6 pt-5 border-t border-[#E5E0D8]" data-testid="shared-corp-cover-meta">
      {client && <p className="text-base sm:text-lg text-[#4a3a1f] mb-3">Prepared for: <strong className="text-[#C5A059] font-semibold">{client}</strong></p>}
      {pairs.length > 0 && (
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-[#5C5C5C]">
          {pairs.map((p) => (
            <span key={p.k} className="whitespace-nowrap"><span className="uppercase tracking-wider text-[10px] text-[#C5A059] mr-1.5">{p.k}</span>{p.v}</span>
          ))}
        </div>
      )}
      {custom && <p className="text-xs text-[#5C5C5C] italic mt-3 max-w-lg mx-auto">{custom}</p>}
    </div>
  );
}

function MoqTable({ tiers, testIdProduct }) {
  if (!tiers || !tiers.length) {
    return (
      <div className="mt-3 pt-3 border-t border-[#E5E0D8] text-[11px] text-[#9E2A2B] italic" data-testid={`shared-moq-empty-${testIdProduct}`}>
        MOQ pricing not configured
      </div>
    );
  }
  const anyDelivery = tiers.some(t => t.delivery_days);
  return (
    <div className="mt-3 pt-3 border-t border-[#E5E0D8]" data-testid={`shared-moq-table-${testIdProduct}`}>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#C5A059] mb-2 font-semibold">MOQ Pricing</p>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-[#1A1A1A] border-b border-[#C5A059]">
            <th className="text-left py-1.5 pr-2 font-semibold">MOQ</th>
            <th className="text-left py-1.5 pr-2 font-semibold">Price / Piece</th>
            {anyDelivery && <th className="text-left py-1.5 font-semibold">Delivery</th>}
          </tr>
        </thead>
        <tbody>
          {tiers.map((t, i) => (
            <tr key={`tier-${t.moq_quantity}-${i}`} className="border-b border-[#F0EBE2]" data-testid={`shared-moq-row-${testIdProduct}-${i}`}>
              <td className="py-1.5 pr-2 font-semibold whitespace-nowrap text-[#1A1A1A]">{Number(t.moq_quantity || 0).toLocaleString()} pcs</td>
              <td className="py-1.5 pr-2 font-semibold whitespace-nowrap text-[#4a3a1f]">
                {t.calculated_price_display}<span className="text-[#888] font-normal ml-0.5">/ pc</span>
              </td>
              {anyDelivery && (
                <td className="py-1.5 text-[11px] text-[#1A1A1A] whitespace-nowrap">{t.delivery_days_display || '—'}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CorporateTermsBlock({ meta }) {
  const m = meta || {};
  const defaultTerms = (m.default_terms || 'Prices are indicative and subject to confirmation at the time of order.').trim();
  const gst = (m.gst_note || '').trim();
  const delivery = (m.delivery_terms || '').trim();
  const validity = formatDateForCover((m.validity_date || '').trim());
  const contact = (m.contact_person || '').trim();
  const client = (m.client_company || '').trim();
  const custom = (m.custom_note || '').trim();
  const lines = [
    client && ['Client', client],
    contact && ['Contact', contact],
    validity && ['Valid until', validity],
    delivery && ['Delivery Terms', delivery],
    gst && ['GST / Tax', gst],
  ].filter(Boolean);
  return (
    <div className="bg-[#FDFBF7] py-12 sm:py-16 px-6 border-t border-[#E5E0D8]" data-testid="shared-corp-terms">
      <h2 className="font-serif text-xl sm:text-2xl tracking-[0.4em] uppercase text-[#C5A059] text-center mb-8 sm:mb-10">Terms &amp; Conditions</h2>
      {lines.length > 0 && (
        <div className="max-w-2xl mx-auto bg-white border border-[#E5E0D8] p-6 sm:p-8 mb-8">
          {lines.map(([k, v]) => (
            <p key={k} className="text-sm text-[#1A1A1A] mb-2.5 last:mb-0 flex flex-wrap gap-x-2">
              <span className="uppercase tracking-wider text-[10px] text-[#C5A059] w-32 shrink-0 pt-0.5">{k}</span>
              <span className="flex-1 min-w-0">{v}</span>
            </p>
          ))}
        </div>
      )}
      <p className="max-w-2xl mx-auto text-sm text-[#5C5C5C] italic text-center leading-relaxed">{defaultTerms}</p>
      {custom && <p className="max-w-2xl mx-auto text-sm text-[#1A1A1A] bg-[#F2E8D5] border-l-4 border-[#C5A059] mt-6 px-5 py-4 leading-relaxed">{custom}</p>}
    </div>
  );
}

export default function SharedCatalogPage() {
  const { token } = useParams();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await publicApi.get(`/shared/${token}`);
      setCatalog(data);
    } catch (e) {
      devError('Shared catalog load failed', e);
      const status = e.response?.status;
      if (status === 404) setError('Catalog not found. The link may have expired or been removed.');
      else if (e.code === 'ECONNABORTED') setError('Request timed out. Check your network and retry.');
      else setError('Unable to load catalog. Please retry.');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  // Public PDF download — uses the cache-aware downloader which:
  // 1. Tries the cached PDF (200 → instant blob download).
  // 2. On 202, polls /shared/{token}/pdf-status, shows live "Preparing PDF…"
  //    toast, then auto-retries the download once cached. Falls back to
  //    window.open(blob) on mobile / WhatsApp WebView.
  const downloadPdf = async () => {
    setPdfBusy(true);
    await downloadPdfWithCache(`/shared/${token}/pdf`, catalog?.title || 'catalog', { publicRoute: true });
    setPdfBusy(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6" data-testid="shared-loading">
        <div className="text-center">
          <Loader2 size={32} className="mx-auto animate-spin text-[#C5A059]" />
          <p className="text-sm text-[#5C5C5C] mt-4 tracking-wide">Loading catalog…</p>
        </div>
      </div>
    );
  }

  if (error || !catalog) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6" data-testid="shared-error">
        <div className="text-center max-w-sm">
          <AlertTriangle size={36} className="mx-auto text-[#C5A059] mb-4" />
          <p className="text-[#1A1A1A] text-base font-medium">{error || 'Unable to load catalog. Please retry.'}</p>
          <p className="text-[#5C5C5C] text-sm mt-2">If the issue continues, contact AARYA24kt.</p>
          <Button onClick={fetchCatalog} className="mt-6 bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="shared-retry-btn">
            <RefreshCw size={16} className="mr-2" />Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="shared-catalog-page">
      {/* Premium Cover Hero */}
      <div className="bg-[#FDFBF7]" data-testid="shared-cover-hero">
        {/* Logo Band */}
        <div className="relative bg-[#141414] py-10 sm:py-12 px-4 sm:px-6 text-center" style={{ borderLeft: '5px solid #C5A059', borderRight: '5px solid #C5A059' }}>
          <img
            src={catalog.branding?.logo_url ? getImageUrl(catalog.branding.logo_url) : '/brand/aarya-logo-gold-on-black.png'}
            alt="AARYA24kt"
            className="h-20 sm:h-28 md:h-36 mx-auto object-contain"
            loading="eager"
            decoding="async"
          />
        </div>
        {/* Title Band */}
        <div className="px-6 sm:px-8 pt-10 sm:pt-12 pb-8 text-center">
          <h1 className="font-serif font-bold tracking-tight leading-tight text-3xl sm:text-5xl lg:text-6xl break-words" style={{ color: '#4a3a1f' }}>{catalog.title}</h1>
          {catalog._title_trans && <p className="font-serif text-xl sm:text-3xl mt-3 break-words" style={{ color: '#8a6b30' }}>{catalog._title_trans}</p>}
          {catalog.subtitle && <p className="text-xs sm:text-sm tracking-[0.3em] uppercase mt-5 text-[#5C5C5C] break-words">{catalog.subtitle}</p>}
          {catalog._subtitle_trans && <p className="text-xs sm:text-sm italic text-[#888] mt-1 break-words">{catalog._subtitle_trans}</p>}
          <div className="w-28 h-px mx-auto my-6 bg-[#C5A059]" />
          {catalog.intro_note && <p className="text-sm text-[#5C5C5C] max-w-2xl mx-auto leading-relaxed">{catalog.intro_note}</p>}
          {catalog._intro_trans && <p className="text-xs sm:text-sm italic text-[#888] max-w-2xl mx-auto leading-relaxed mt-2">{catalog._intro_trans}</p>}
          {catalog.catalog_type === 'corporate' && <CorporateCoverMeta meta={catalog.corporate_meta} />}
          <div className="mt-8 flex gap-3 justify-center flex-wrap">
            <Button onClick={downloadPdf} disabled={pdfBusy} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="download-pdf-btn">
              {pdfBusy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}Download PDF
            </Button>
            <Button onClick={() => { window.location.href = `/flipbook/${token}`; }} variant="outline" className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white rounded-sm" data-testid="view-flipbook-btn">
              <BookOpen size={16} className="mr-2" />View Flipbook
            </Button>
          </div>
        </div>
        {/* Cover Image — bounded height so the photo never dominates the
            page; ~45vh mobile / ~55vh desktop, aspect-ratio preserved via
            object-fit: contain, centered on a soft cream backdrop. */}
        <div className="w-full bg-[#F5F2EB] flex items-center justify-center px-4 sm:px-8 py-6 sm:py-10" data-testid="shared-cover-image">
          {catalog.cover_image ? (
            <img
              src={getImageUrl(catalog.cover_image)}
              alt="Cover"
              className="block mx-auto object-contain w-auto h-auto max-w-[90%] sm:max-w-[75%] max-h-[45vh] sm:max-h-[55vh]"
              loading="eager"
              decoding="async"
            />
          ) : (
            <div className="w-full max-w-[75%] mx-auto flex items-center justify-center font-serif text-4xl sm:text-6xl tracking-[0.4em] text-[#C5A059] rounded-sm" style={{ background: 'linear-gradient(135deg, #2a1f10 0%, #141414 100%)', minHeight: '30vh', maxHeight: '45vh', aspectRatio: '4 / 3' }}>
              {catalog.branding?.company_name || 'AARYA24kt'}
            </div>
          )}
        </div>
      </div>

      {/* Contents / Index — mirrors the Preview + PDF TOC so shared-link
          viewers see the same navigation cue. Hidden when there is nothing
          to index (0 sections). */}
      {(catalog.sections || []).length > 0 && (
        <div className="bg-[#FDFBF7] border-y border-[#E5E0D8]" data-testid="shared-toc">
          <div className="max-w-3xl mx-auto py-10 sm:py-14 px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 bg-[#E5E0D8]" />
              <h2 className="font-serif text-lg sm:text-xl tracking-[0.4em] uppercase text-center" style={{ color: catalog.branding?.primary_color || '#C5A059' }}>Contents</h2>
              <div className="h-px flex-1 bg-[#E5E0D8]" />
            </div>
            <div className="space-y-1">
              {(catalog.sections || []).map((s, i) => {
                // Product Master V2 (iter24): count unique cards, not raw SKUs
                // (a design group with 5 SKUs counts as 1 card).
                const uniqueCards = iterateSectionCards(s.products).length;
                return (
                <div key={s.id || i} className="flex justify-between items-baseline gap-4 py-3 border-b border-[#E5E0D8]/70" data-testid={`shared-toc-item-${i}`}>
                  <div className="min-w-0">
                    <div className="font-serif text-base sm:text-lg text-[#1A1A1A] truncate">{s.name}</div>
                    {s._name_trans && <div className="text-xs sm:text-sm text-[#888] italic truncate">{s._name_trans}</div>}
                  </div>
                  <div className="text-xs sm:text-sm text-[#5C5C5C] whitespace-nowrap">
                    {uniqueCards} {uniqueCards === 1 ? 'product' : 'products'}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Sections */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        {(catalog.sections || []).map((section, sIdx) => (
          <div key={section.id || sIdx} className="mb-12 sm:mb-16" data-testid={`shared-section-${sIdx}`}>
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="h-px flex-1 bg-[#E5E0D8]" />
              <div className="text-center">
                <h2 className="font-serif text-xl sm:text-2xl text-[#1A1A1A] tracking-tight">{section.name}</h2>
                {section._name_trans && <p className="text-sm sm:text-base text-[#888] italic mt-1">{section._name_trans}</p>}
              </div>
              <div className="h-px flex-1 bg-[#E5E0D8]" />
            </div>
            <div className={catalog.catalog_type === 'corporate'
              ? "grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 items-start auto-rows-max"
              : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 items-start auto-rows-max"}>
              {iterateSectionCards(section.products).map((card, pIdx) => {
                const { product, isGroup } = card;
                const pt = product._translations || {};
                const isCorp = catalog.catalog_type === 'corporate';
                const displayCode = cardCode(card);
                const displayName = cardName(card);
                const displayShort = cardShort(card);
                const displayImage = cardImage(card);
                const showPrices = catalog.show_prices !== false;
                return (
                  <div key={(isGroup ? `g-${product.design_group_id}` : (product.id || pIdx))} className="bg-white border border-[#E5E0D8] rounded-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col" data-testid={`shared-product-${pIdx}`}>
                    <div className="aspect-square bg-[#F5F2EB] flex items-center justify-center">
                      {displayImage ? (
                        <img
                          src={getImageUrl(displayImage)}
                          alt={displayName}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <ImageIcon size={32} className="text-[#E5E0D8]" />
                      )}
                    </div>
                    <div className="p-3 sm:p-4 flex-1 flex flex-col">
                      <p className="text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-[#C5A059] mb-1 truncate">{displayCode}</p>
                      <h3 className="text-xs sm:text-sm font-medium text-[#1A1A1A] line-clamp-2">{displayName}</h3>
                      {!isGroup && pt.product_name && <p className="text-[10px] sm:text-xs text-[#888] italic line-clamp-2">{pt.product_name}</p>}
                      {displayShort && <p className="text-[10px] sm:text-xs text-[#5C5C5C] mt-1 line-clamp-2">{displayShort}</p>}
                      {!isGroup && pt.short_description && <p className="text-[9px] sm:text-[10px] text-[#aaa] italic line-clamp-2">{pt.short_description}</p>}
                      {isGroup ? (
                        // Product Master V2 — Available Sizes table (renders
                        // ALL active SKUs of the group, not just the one the
                        // user picked). Column count adapts to show_prices.
                        <div className="mt-3 pt-3 border-t border-[#E5E0D8]" data-testid="shared-sizes-table">
                          <div className="text-[9px] tracking-[0.2em] uppercase mb-2" style={{ color: catalog.branding?.primary_color || '#C5A059' }}>Available Sizes</div>
                          <table className="w-full text-[11px] sm:text-xs">
                            <thead>
                              <tr className="text-left text-[#1A1A1A]" style={{ borderBottom: `1px solid ${catalog.branding?.primary_color || '#C5A059'}` }}>
                                <th className="py-1.5 pr-2 font-semibold uppercase tracking-wide text-[9px] sm:text-[10px]">SKU</th>
                                <th className="py-1.5 pr-2 font-semibold uppercase tracking-wide text-[9px] sm:text-[10px]">Size</th>
                                {showPrices && <th className="py-1.5 text-right font-semibold uppercase tracking-wide text-[9px] sm:text-[10px]">MRP</th>}
                                {catalog.show_quantity && <th className="py-1.5 pl-2 text-right font-semibold uppercase tracking-wide text-[9px] sm:text-[10px]">Qty</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {(product._group_siblings || []).map((sib) => (
                                <tr key={sib.product_code} className="border-b border-[#E5E0D8]/70 last:border-b-0" data-testid={`shared-sizes-row-${sib.product_code}`}>
                                  <td className="py-1.5 pr-2 font-mono text-[10px] sm:text-[11px] whitespace-nowrap" style={{ color: catalog.branding?.primary_color || '#C5A059' }}>{sib.product_code}</td>
                                  <td className="py-1.5 pr-2 text-[#5C5C5C]">{sib.size || '—'}</td>
                                  {showPrices && <td className="py-1.5 text-right font-medium text-[#1A1A1A] whitespace-nowrap">{sib._display_price || '—'}</td>}
                                  {catalog.show_quantity && (
                                    sib._quantity_available
                                      ? <td className="py-1.5 pl-2 text-right whitespace-nowrap" data-testid={`shared-sizes-qty-${sib.product_code}`}><span className="inline-block bg-[#F2E8D5] text-[#3a2d14] text-[10px] sm:text-xs font-semibold rounded-sm px-2 py-0.5">{sib._quantity_display}</span></td>
                                      : <td className="py-1.5 pl-2 text-right whitespace-nowrap" data-testid={`shared-sizes-qty-missing-${sib.product_code}`}><span className="inline-block bg-[#F5F2EB] text-[#8a7a5c] text-[10px] sm:text-xs rounded-sm px-2 py-0.5">Qty unavailable</span></td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#E5E0D8] gap-2">
                          {product.size && <span className="text-[10px] sm:text-xs text-[#5C5C5C] truncate">{product.size}</span>}
                          <div className="flex items-center gap-1.5 ml-auto">
                            {product._display_price && <span className="text-xs sm:text-sm font-medium text-[#1A1A1A] whitespace-nowrap">{product._display_price}</span>}
                            {catalog.show_quantity && (
                              product._quantity_available
                                ? <span className="inline-block bg-[#F2E8D5] text-[#3a2d14] text-[10px] sm:text-xs font-semibold rounded-sm px-2 py-0.5 whitespace-nowrap" data-testid={`shared-qty-badge-${pIdx}`}>{product._quantity_display}</span>
                                : <span className="inline-block bg-[#F5F2EB] text-[#8a7a5c] text-[10px] sm:text-xs rounded-sm px-2 py-0.5 whitespace-nowrap" data-testid={`shared-qty-missing-${pIdx}`}>Qty unavailable</span>
                            )}
                          </div>
                        </div>
                      )}
                      {isCorp && catalog.show_prices !== false && <MoqTable tiers={product._moq_tiers} testIdProduct={product.id || pIdx} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {catalog.catalog_type === 'corporate' && <CorporateTermsBlock meta={catalog.corporate_meta} />}

      {/* Back Cover — always rendered with brand logo */}
      <div className="bg-[#141414] py-16 sm:py-20 text-center" data-testid="shared-back-cover">
        <div className="max-w-3xl mx-auto px-6">
          <img
            src={catalog.branding?.logo_url ? getImageUrl(catalog.branding.logo_url) : '/brand/aarya-logo-gold-on-black.png'}
            alt="AARYA24kt"
            className="h-24 sm:h-40 mx-auto object-contain"
            loading="lazy"
            decoding="async"
          />
          <div className="w-20 h-px mx-auto my-8 bg-[#C5A059]" />
          {catalog.back_cover?.name && <p className="font-serif text-xl sm:text-2xl text-[#C5A059] mb-3">{catalog.back_cover.name}</p>}
          {catalog.back_cover?.phone && <p className="text-sm text-[#E5E0D8] mb-1 tracking-wide break-words">{catalog.back_cover.phone}</p>}
          {catalog.back_cover?.email && <p className="text-sm text-[#E5E0D8] mb-1 tracking-wide break-words">{catalog.back_cover.email}</p>}
          {catalog.back_cover?.website && <p className="text-sm text-[#E5E0D8] mb-1 tracking-wide break-words">{catalog.back_cover.website}</p>}
          {catalog.back_cover?.address && <p className="text-xs text-[#E5E0D8] mt-4 whitespace-pre-line max-w-lg mx-auto leading-relaxed break-words">{catalog.back_cover.address}</p>}
          {!catalog.back_cover?.name && catalog.footer_contact && <p className="text-sm text-[#E5E0D8] whitespace-pre-line break-words">{catalog.footer_contact}</p>}
          {!catalog.back_cover?.name && !catalog.footer_contact && <p className="text-sm italic tracking-[0.25em] text-[#C5A059]">{catalog.branding?.tagline || 'Gold Foil Artwork Collection'}</p>}
          <p className="font-serif text-base tracking-[0.45em] uppercase mt-10 sm:mt-12 text-[#C5A059]">Thank you</p>
        </div>
      </div>
    </div>
  );
}
